import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Spark, SparkReview, VoteType } from "@sparklab/types";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { DEFAULT_THEME, getThemeStyles } from "../themeConfig";
import { LikeButton } from "../components/LikeButton";
import { ShareButton } from "../components/ShareButton";

export default function SparkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { showToast } = useToast();
  const themeKey = user?.theme ?? DEFAULT_THEME;
  const t = getThemeStyles(themeKey);

  const [spark, setSpark] = useState<Spark | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voting, setVoting] = useState(false);
  const [reviews, setReviews] = useState<SparkReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentMessage, setCommentMessage] = useState<string | null>(null);
  const [commentError, setCommentError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError("缺少作品 ID");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSpark = async () => {
      try {
        setLoading(true);
        // 使用编码的 ID（直接传递字符串，API 会处理解码）
        const data = await api.getSpark(id);
        if (!cancelled) {
          setSpark(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          // 检查是否是 403 错误（作品未公开）
          let message = err instanceof Error ? err.message : "获取作品失败";
          // 如果错误信息包含"未公开"或状态码是 403，显示友好提示
          const status = (err as any)?.status;
          if (message.includes("未公开") || status === 403) {
            message = "没有这个 spark";
          }
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSpark();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!spark?.id) {
      setReviews([]);
      setCommentText("");
      setCommentMessage(null);
      setCommentError(null);
      return;
    }

    let cancelled = false;

    const fetchReviews = async () => {
      try {
        setReviewsLoading(true);
        const data = await api.getSparkReviews(spark.id);
        if (!cancelled) {
          setReviews(data);
          setCommentError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "获取评论失败";
          setCommentError(message);
        }
      } finally {
        if (!cancelled) {
          setReviewsLoading(false);
        }
      }
    };

    fetchReviews();

    return () => {
      cancelled = true;
    };
  }, [spark?.id]);

  const handleVote = async (vote: VoteType) => {
    if (!spark) return;

    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    try {
      setVoting(true);
      const updated = await api.voteSpark(spark.id, vote);
      setSpark(updated);
    } catch (err) {
      const message = err instanceof Error ? err.message : "投票失败";
      showToast(message, "error");
    } finally {
      setVoting(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!spark) return;
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    const content = commentText.trim();
    if (!content) {
      setCommentError("评论内容不能为空");
      return;
    }
    if (content.length > 500) {
      setCommentError("评论内容不能超过 500 字符");
      return;
    }

    try {
      setCommentSubmitting(true);
      const review = await api.addSparkComment(spark.id, content);
      setCommentText("");
      setCommentMessage("评论已提交");
      setCommentError(null);
      setReviews((prev) => {
        const others = prev.filter((item) => item.id !== review.id);
        return [review, ...others];
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "发表评论失败";
      setCommentError(message);
    } finally {
      setCommentSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen ${t.bg} text-white p-6 font-mono transition-all duration-300`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button2} hover:scale-105 transition`}
            >
              ← 返回
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/sparks")}
              className="px-4 py-2 rounded-xl border-2 font-bold border-white/40 hover:scale-105 transition"
            >
              🌐 浏览公开作品
            </button>
            <button
              onClick={() => navigate(isAuthenticated ? "/my-sparks" : "/login")}
              className="px-4 py-2 rounded-xl border-2 font-bold border-white/40 hover:scale-105 transition"
            >
              📁 我的作品
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-sm opacity-80">正在载入作品...</div>
        ) : error ? (
          <div className="text-center text-red-200">
            <p className="mb-4">{error}</p>
            <button
              onClick={() => navigate("/")}
              className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button3} hover:scale-105 transition`}
            >
              返回首页
            </button>
          </div>
        ) : spark ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-3xl p-4 shadow-2xl border-4">
                <div className="rounded-2xl overflow-hidden border border-black/5 bg-black/5">
                  <iframe
                    srcDoc={spark.html}
                    title={spark.name}
                    className="w-full h-[520px] md:h-[640px] lg:h-[720px] border-none"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className={`rounded-3xl p-6 border-4 shadow-xl flex flex-col gap-4 ${t.card}`}>
                <div>
                  <p className="text-sm opacity-80">
                    {new Date(spark.created_at).toLocaleString("zh-CN", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                  <h1 className={`text-3xl font-bold mt-2 ${t.header}`}>{spark.name}</h1>
                  <p className="text-sm opacity-80 mt-2">🪄 {spark.prompt}</p>
                </div>

                {spark.creator_name && (
                  <div className="text-sm opacity-70 flex items-center gap-1 mt-2">
                    <span>👤</span>
                    <span className="font-medium">{spark.creator_name}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 mt-5">
                  <LikeButton
                    direction="up"
                    size="lg"
                    count={spark.likes}
                    loading={voting}
                    disabled={voting}
                    onClick={() => handleVote("like")}
                  />
                  <LikeButton
                    direction="down"
                    size="lg"
                    count={spark.unlikes}
                    loading={voting}
                    disabled={voting}
                    onClick={() => handleVote("unlike")}
                  />
                </div>

                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-sm font-semibold mb-3 opacity-90">分享这个作品</p>
                  <ShareButton
                    url={window.location.href}
                    title={spark.name}
                    description={spark.prompt}
                  />
                </div>
              </div>

              <div className={`rounded-3xl p-6 border-4 shadow-xl ${t.card}`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-xl font-bold ${t.header}`}>留言互动</h2>
                  <span className="text-xs opacity-75 font-semibold">{reviews.length} 条评论</span>
                </div>

                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder={isAuthenticated ? "写点什么吧，最多 500 字" : "登录后即可留言互动"}
                  className={`w-full px-4 py-3 rounded-2xl border-2 ${t.input} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                />
                <div className="flex items-center justify-between text-xs opacity-75 mt-2">
                  <span>{commentText.trim().length}/500</span>
                  <button
                    onClick={handleSubmitComment}
                    disabled={commentSubmitting || !commentText.trim()}
                    className={`px-4 py-2 rounded-xl border-2 font-bold ${
                      commentText.trim() ? t.button1 : "border-white/40 text-white/60"
                    } hover:scale-105 transition disabled:opacity-50 disabled:hover:scale-100`}
                  >
                    {commentSubmitting ? "提交中..." : isAuthenticated ? "提交评论" : "登录后评论"}
                  </button>
                </div>
                {commentMessage && <p className="text-green-200 text-sm mt-2">{commentMessage}</p>}
                {commentError && <p className="text-red-200 text-sm mt-2">{commentError}</p>}

                <div className="mt-6 space-y-3 max-h-[360px] overflow-y-auto pr-1">
                  {reviewsLoading ? (
                    <p className="text-sm opacity-80">正在加载评论...</p>
                  ) : reviews.length === 0 ? (
                    <p className="text-sm opacity-80">还没有评论，成为第一个发声的人吧！</p>
                  ) : (
                    reviews.map((review) => (
                      <div key={review.id} className="p-4 rounded-2xl border border-white/30 bg-black/20">
                        <div className="flex justify-between items-center text-sm font-bold mb-2">
                          <div className="flex items-center gap-2">
                            <span>👤</span>
                            <span className="font-medium">{review.reviewer_name || "匿名用户"}</span>
                          </div>
                          <span className="opacity-70">
                            {new Date(review.updated_at).toLocaleString("zh-CN", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        {review.comment && (
                          <p className="text-sm opacity-90 mt-2 whitespace-pre-wrap break-words">{review.comment}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}


